
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {OBJLoader} from "../../../libs/three.js/loaders/OBJLoader.js";
import {MTLLoader} from "../../../libs/three.js/loaders/MTLLoader.js";
import {resolveIfcGeoreference} from "./IfcGeoreference.js";
import {Utils} from "../../utils.js";

import {t} from "../../digitization/i18n/index.js";
// Below this apparent on-screen radius (see Utils.apparentPixelSize), an
// element's wireframe overlay is hidden rather than drawn - same fix and
// same reasoning as LER.js's addEdgesOverlay: a fixed facet-edge count,
// squeezed into a shrinking screen area as the element gets smaller/further
// away, eventually overlaps itself enough (each edge semi-transparent) to
// blend toward solid black and swallow the element's actual color. Lower =
// wireframe stays visible further out (apparent size shrinks as
// 1/distance, so halving this roughly doubles the fade-out distance).
const EDGES_MIN_APPARENT_RADIUS_PX = 10;
const scratchWorldPos = new THREE.Vector3();
const scratchWorldScale = new THREE.Vector3();

// Loads an IFC (Industry Foundation Classes - BIM/infrastructure model)
// file entirely client-side via web-ifc (a WASM build of the open-source
// IFC geometry engine), no server-side preprocessing - same principle as
// the LER GML/WFS and FXL import paths. Real-world files carrying a proper
// IfcSite/IfcProjectedCRS placement (like Danish utility exports from
// DANDAS/Autodesk InfraWorks) come out already positioned in true
// EPSG-style world coordinates, so they line up with a point cloud
// covering the same area with no manual alignment step needed.
//
// Precision: web-ifc computes geometry in double precision internally but
// hands vertex data back as float32 - if a model's real-world coordinates
// (easting/northing in the hundreds of thousands) were baked directly into
// that float32 vertex data, a 30cm pipe would be well below the
// representable precision at that magnitude and geometry would visibly
// crumble. OpenModel's COORDINATE_TO_ORIGIN setting has web-ifc compute
// everything relative to a near-zero internal origin instead (vertex data
// stays small and precise) and GetCoordinationMatrix() gives back the
// transform to true world space - applied here as the *container's* own
// transform (position/quaternion/scale), not baked into any vertex buffer,
// exactly like every other large-coordinate layer in this app (LER,
// CutFillVolume, Digitizing) stays precise via a THREE.Object3D.position
// offset rather than raw absolute vertex coordinates.

let ifcAPI = null;
let ifcAPIReady = null;
let webIfcNamespace = null; // the imported module itself - IFCMAPCONVERSION/IFCSITE/etc type constants live here

function wasmDirUrl(){
	return new URL("../../../libs/web-ifc/", import.meta.url).href;
}

// web-ifc-api.js is ~6MB (the full IFC4 schema plus WASM glue) - loaded
// with a dynamic import so it's only ever fetched/parsed the first time
// someone actually opens an IFC file, rather than bundled into the
// always-loaded core potree.js (see the `external` rule in
// rollup.config.js, which is what keeps this import() from being inlined
// at build time).
//
// A failed load (dev server restarting/rebuilding, a momentary 404, a
// dropped connection) must not stick: the rejected promise is dropped so
// the next attempt actually retries, and the retry uses a fresh ?retry=N
// URL because the browser's module map remembers a failed import per URL
// and would otherwise re-reject instantly without touching the network.
let ifcAPIAttempt = 0;

async function ensureIfcAPI(){
	if(ifcAPIReady){
		return ifcAPIReady;
	}

	let retrySuffix = ifcAPIAttempt > 0 ? `?retry=${ifcAPIAttempt}` : "";
	ifcAPIReady = import(/* webpackIgnore: true */ wasmDirUrl() + "web-ifc-api.js" + retrySuffix).then(WebIFC => {
		webIfcNamespace = WebIFC;
		ifcAPI = new WebIFC.IfcAPI();
		ifcAPI.SetWasmPath(wasmDirUrl(), true);
		return ifcAPI.Init().then(() => ifcAPI);
	}).catch(error => {
		ifcAPIReady = null;
		ifcAPIAttempt++;
		throw error;
	});

	return ifcAPIReady;
}

// geometryExpressID -> THREE.BufferGeometry, per model - an IFC geometry
// definition can be placed multiple times (e.g. a repeated fitting), and
// GetGeometry/GetVertexArray is comparatively expensive to redo per placement.
const geometryCacheByModel = new Map();

function buildGeometry(api, modelID, geometryExpressID){
	let cache = geometryCacheByModel.get(modelID);
	if(!cache){
		cache = new Map();
		geometryCacheByModel.set(modelID, cache);
	}

	let cached = cache.get(geometryExpressID);
	if(cached){
		return cached;
	}

	let ifcGeometry = api.GetGeometry(modelID, geometryExpressID);
	let vertexData = api.GetVertexArray(ifcGeometry.GetVertexData(), ifcGeometry.GetVertexDataSize());
	let indexData = api.GetIndexArray(ifcGeometry.GetIndexData(), ifcGeometry.GetIndexDataSize());

	// web-ifc's raw vertex buffer interleaves position and normal per
	// vertex: [x, y, z, nx, ny, nz, ...] - split into two plain attributes
	// for a standard THREE.BufferGeometry. Both this and the index copy
	// below must happen *before* ifcGeometry.delete(): GetVertexArray/
	// GetIndexArray only hand back views into the WASM heap at the given
	// pointer, which delete() frees - reading them afterwards silently
	// returns stale/zeroed data instead of throwing, which is what a first
	// version of this got wrong (some geometries loaded fine, others came
	// back with zero vertices, depending on how soon the freed memory got
	// reused by a later allocation).
	let vertexCount = vertexData.length / 6;
	let positions = new Float32Array(vertexCount * 3);
	let normals = new Float32Array(vertexCount * 3);
	for(let i = 0; i < vertexCount; i++){
		positions[i * 3 + 0] = vertexData[i * 6 + 0];
		positions[i * 3 + 1] = vertexData[i * 6 + 1];
		positions[i * 3 + 2] = vertexData[i * 6 + 2];
		normals[i * 3 + 0] = vertexData[i * 6 + 3];
		normals[i * 3 + 1] = vertexData[i * 6 + 4];
		normals[i * 3 + 2] = vertexData[i * 6 + 5];
	}
	let indices = new Uint32Array(indexData); // copy out of WASM memory, not a view

	ifcGeometry.delete();

	let geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
	geometry.setIndex(new THREE.BufferAttribute(indices, 1));

	// A low threshold angle picks up a swept solid's segment-to-segment
	// facets (a pipe's circular cross-section is only approximated by a
	// handful of flat facets, each meeting its neighbour at a real angle)
	// while still merging away the perfectly coplanar diagonal each flat
	// rectangular face is triangulated into - producing the "wireframe on
	// cylinders, clean edges on flat panels" look asked for, as one
	// general rule rather than special-casing pipe element types.
	let edgesGeometry = new THREE.EdgesGeometry(geometry, 1);

	// Local-space size, for the wireframe fade in loadIfcFile below - not a
	// screen size itself, just this element's own scale before its placement
	// transform (which can carry scaling) is applied.
	geometry.computeBoundingSphere();
	let localRadius = geometry.boundingSphere.radius;

	let built = {geometry, edgesGeometry, localRadius};
	cache.set(geometryExpressID, built);
	return built;
}

// One loaded IFC model. Meshes use MeshBasicMaterial (unlit), same
// reasoning as the LER pipe/component rendering: Potree's main scene has
// no THREE.Light set up, so a lit material would render everything black.
// Default opacity multiplier applied to a ghosted element (Feature H's
// isolate/hide/ghost, and Feature F's "ghost the non-matching elements"
// option) - the spec's own default (10%), low enough to clearly read as
// "faded context", never fully invisible (that's what the separate hidden
// state is for - see the visual-priority order in the PointView spec:
// hidden > selected/hover > filter-color > ghost-opacity > original).
// Feature H's own panel makes this adjustable per model via setGhostOpacity.
const DEFAULT_GHOST_OPACITY = 0.10;

export class IFCModel extends THREE.Object3D{

	constructor(){
		super();
		this.name = "ifc_model";
		this.modelID = null;
		this.opacity = 1.0;
		this.ghostOpacity = DEFAULT_GHOST_OPACITY;
		this.meshes = [];
		this.meshesByExpressId = new Map(); // expressID -> Mesh[] (one element can have multiple placed geometries)
	}

	// Recomputes one mesh's actual material opacity/depthWrite/transparent
	// from the global opacity slider, its own base (per-element) opacity,
	// and whether it's currently ghosted (by Feature F's filter or Feature
	// H's own isolate/ghost actions - either reason applies the same
	// ghostOpacity) - the single place all these concerns meet, so they
	// compose instead of fighting over mesh.material.opacity directly.
	_updateMeshMaterial(mesh){
		let base = mesh.userData.baseOpacity ?? 1;
		let isGhosted = !!mesh.userData.ghosted || !!mesh.userData.isolateGhosted;
		let ghostFactor = isGhosted ? this.ghostOpacity : 1;
		let opacity = this.opacity * base * ghostFactor;
		mesh.material.opacity = opacity;
		mesh.material.transparent = opacity < 1;
		mesh.material.depthWrite = opacity >= 1;

		if(mesh.userData.edges){
			let edgeOpacity = this.opacity * ghostFactor;
			mesh.userData.edges.material.opacity = edgeOpacity;
			mesh.userData.edges.material.transparent = edgeOpacity < 1;
		}
	}

	setOpacity(opacity){
		this.opacity = opacity;
		for(let mesh of this.meshes){
			this._updateMeshMaterial(mesh);
		}
	}

	// Feature H's adjustable ghost-opacity (spec default 10%, "kan
	// justeres") - affects every currently-ghosted element immediately,
	// regardless of whether it's ghosted by Feature F's filter or by one of
	// H's own isolate/ghost actions, since both share this one setting.
	setGhostOpacity(value){
		this.ghostOpacity = value;
		for(let mesh of this.meshes){
			this._updateMeshMaterial(mesh);
		}
	}

	// Returns the IFC element's expressID (the owning product - a pipe
	// segment, chamber, etc - not the lower-level geometry definition) at
	// the raycaster's closest hit, or null. Ghosted elements are excluded
	// by default - per the spec, "ghostede elementer må som standard ikke
	// kunne vælges" - pass includeGhosted=true (Feature H's "Tillad snap på
	// kontekst") to temporarily lift that. Hidden elements are always
	// excluded regardless (filtered explicitly rather than relying on
	// three.js's own raycast-visibility behavior).
	pickExpressIdAt(raycaster, includeGhosted = false){
		let candidates = this.meshes.filter(m => m.visible && (includeGhosted || !(m.userData.ghosted || m.userData.isolateGhosted)));
		let hits = raycaster.intersectObjects(candidates, false);
		return hits.length > 0 ? hits[0].object.userData.expressID : null;
	}

	// The subset of meshes digitizing/measurement snapping should consider
	// (see index.html's refreshPickableMeshes) - same ghosted-exclusion
	// rule as pickExpressIdAt, and for the same spec requirement ("...eller
	// bruges til snapping").
	getSnappableMeshes(includeGhosted = false){
		return this.meshes.filter(m => m.visible && (includeGhosted || !(m.userData.ghosted || m.userData.isolateGhosted)));
	}

	// Recomputes one mesh's actual .visible from all four independent hide
	// layers at once - manual (tree checkboxes), filter (Feature F),
	// color-legend (Feature G's per-value click-to-hide), and isolate
	// (Feature H's isolate/hide actions). Any one of them hiding an element
	// wins, matching the spec's visual priority order (hidden always wins
	// over everything else) - and each layer's own setter only ever touches
	// its own flag, so clearing one never clobbers what the others
	// independently wanted.
	_updateMeshVisibility(mesh){
		mesh.visible = !mesh.userData.manuallyHidden && !mesh.userData.filterHidden
			&& !mesh.userData.colorHidden && !mesh.userData.isolateHidden;
	}

	// Show/hide every placed geometry belonging to one IFC element - the
	// layer tree's own per-element/per-group checkboxes ("manual"
	// visibility). Composes with an active filter's/color legend's own hide
	// state rather than fighting it: a manually-checked element still stays
	// hidden while it's filtered out, and re-checking it only takes effect
	// once the other layers show it again too - matching the spec's
	// priority order (hidden always wins).
	setExpressIdVisible(expressID, visible){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.manuallyHidden = !visible;
			this._updateMeshVisibility(mesh);
		}
	}

	// True if the tree's own checkbox(es) currently want this element shown
	// (independent of whatever an active filter is doing to it) - lets the
	// filter UI compose with, rather than clobber, the user's manual
	// show/hide choices when it recomputes visibility.
	isManuallyVisible(expressID){
		let meshes = this.meshesByExpressId.get(expressID) || [];
		return meshes.length === 0 || !meshes[0].userData.manuallyHidden;
	}

	// True if an active filter is currently hiding this element (as opposed
	// to it being manually unchecked in the tree, or simply not existing) -
	// lets tree-row UI show the real reason an element isn't visible.
	isFilterHidden(expressID){
		let meshes = this.meshesByExpressId.get(expressID) || [];
		return meshes.length > 0 && meshes[0].userData.filterHidden === true;
	}

	// True if Feature G's color legend (clicking a value, or "Øvrige") is
	// currently hiding this element.
	isColorHidden(expressID){
		let meshes = this.meshesByExpressId.get(expressID) || [];
		return meshes.length > 0 && meshes[0].userData.colorHidden === true;
	}

	// Fades (not hides) every placed geometry of one element - Feature F's
	// "ghost the non-matching elements" option and, later, Feature H's
	// context-ghosting. Never touches mesh.visible, so it can't conflict
	// with manual or filter-hide state.
	setExpressIdGhosted(expressID, ghosted){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.ghosted = ghosted;
			this._updateMeshMaterial(mesh);
		}
	}

	// Hides every placed geometry of one element because an active filter
	// excludes it - kept as its own flag (rather than reusing
	// setExpressIdVisible directly) so clearing the filter restores exactly
	// whatever the tree checkboxes had set beforehand, instead of forcing
	// everything back to visible.
	setExpressIdFilterHidden(expressID, hidden){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.filterHidden = hidden;
			this._updateMeshVisibility(mesh);
		}
	}

	// Hides every placed geometry of one element because Feature G's color
	// legend says so (the user clicked a value, or "Øvrige", to temporarily
	// hide it) - its own flag for the same reason setExpressIdFilterHidden
	// is: clearing it (or deactivating coloring) restores exactly whatever
	// the other layers independently wanted, nothing more.
	setExpressIdColorHidden(expressID, hidden){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.colorHidden = hidden;
			this._updateMeshVisibility(mesh);
		}
	}

	// Clears every color-legend hide in one pass - used when coloring is
	// deactivated or switched to a different property (a stale hide from
	// the *previous* property's legend would otherwise silently keep
	// elements hidden with no visible legend entry pointing at why).
	clearAllColorHidden(){
		for(let mesh of this.meshes){
			mesh.userData.colorHidden = false;
			this._updateMeshVisibility(mesh);
		}
	}

	// Sets (or, with colorHex === null, restores the original IFC-authored
	// color of) every placed geometry of one element - Feature G's actual
	// per-value coloring. Never touches opacity/visibility, so it composes
	// cleanly with ghosting (a ghosted element keeps its assigned color,
	// just faded - see the spec's own visual priority order) and with every
	// hide layer above.
	setElementColor(expressID, colorHex){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			if(colorHex){
				mesh.material.color.set(colorHex);
			}else if(mesh.userData.baseColor){
				mesh.material.color.copy(mesh.userData.baseColor);
			}
		}
	}

	// Restores every element's original IFC-authored color in one pass -
	// used when Feature G's coloring is deactivated, so "deaktivering
	// gendanner den oprindelige modelvisning" (spec acceptance criterion)
	// holds without reloading the model.
	clearAllElementColors(){
		for(let mesh of this.meshes){
			if(mesh.userData.baseColor){
				mesh.material.color.copy(mesh.userData.baseColor);
			}
		}
	}

	// True if Feature H's own isolate/hide actions ("isolér ...", "skjul
	// valgte elementer") are currently hiding this element - as opposed to
	// the tree checkbox, the filter, or the color legend.
	isIsolateHidden(expressID){
		let meshes = this.meshesByExpressId.get(expressID) || [];
		return meshes.length > 0 && meshes[0].userData.isolateHidden === true;
	}

	// True if Feature H's "ghost alt andet" is currently ghosting this
	// element.
	isIsolateGhosted(expressID){
		let meshes = this.meshesByExpressId.get(expressID) || [];
		return meshes.length > 0 && meshes[0].userData.isolateGhosted === true;
	}

	// Feature H's own hide flag - kept separate from filterHidden/
	// colorHidden/manuallyHidden ("skjul/isoler må ikke ændre det
	// underliggende filter; handlingerne er et separat visibility-lag") so
	// resetManualVisibility can clear exactly this layer (plus the tree's
	// manual hides) without touching the filter or color legend at all.
	setExpressIdIsolateHidden(expressID, hidden){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.isolateHidden = hidden;
			this._updateMeshVisibility(mesh);
		}
	}

	// Feature H's own ghost flag ("ghost alt andet") - mutually exclusive
	// with isolateHidden in practice (the isolate* bulk methods below always
	// clear the other), but tracked independently since they affect
	// different material properties (visibility vs. opacity).
	setExpressIdIsolateGhosted(expressID, ghosted){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.isolateGhosted = ghosted;
			this._updateMeshMaterial(mesh);
		}
	}

	// "Isolér valgte/type/etage" - hides every element NOT in keepExpressIds
	// (a Set), leaving those elements' own filter/color/manual state alone.
	// Clears any previous "ghost alt andet" state first, since the two are
	// mutually exclusive ways of isolating and re-running one should fully
	// replace the other rather than layering.
	isolateShow(keepExpressIds){
		for(let expressID of this.meshesByExpressId.keys()){
			let keep = keepExpressIds.has(expressID);
			this.setExpressIdIsolateHidden(expressID, !keep);
			this.setExpressIdIsolateGhosted(expressID, false);
		}
	}

	// "Ghost alt andet" - ghosts every element NOT in keepExpressIds,
	// leaving those elements fully normal. The ghost-based counterpart to
	// isolateShow above.
	ghostAllExcept(keepExpressIds){
		for(let expressID of this.meshesByExpressId.keys()){
			let keep = keepExpressIds.has(expressID);
			this.setExpressIdIsolateGhosted(expressID, !keep);
			this.setExpressIdIsolateHidden(expressID, false);
		}
	}

	// "Skjul valgte elementer" - hides exactly the given elements and
	// nothing else (not an isolate: everything else keeps whatever
	// visibility it already had).
	hideOnly(expressIds){
		for(let expressID of expressIds){
			this.setExpressIdIsolateHidden(expressID, true);
		}
	}

	// "Vis alt/nulstil visning" - clears every *manual* hide (the tree's own
	// checkboxes) and every Feature H isolate/ghost action in one pass,
	// without touching the active filter (Feature F) or color legend
	// (Feature G) at all - per the spec's explicit "nulstiller manuelle
	// skjul/isoler-handlinger, men ... fjerner ikke et aktivt filter".
	resetManualVisibility(){
		for(let mesh of this.meshes){
			mesh.userData.manuallyHidden = false;
			mesh.userData.isolateHidden = false;
			mesh.userData.isolateGhosted = false;
			this._updateMeshVisibility(mesh);
			this._updateMeshMaterial(mesh);
		}
	}

	// Feature H's/the app's Ctrl+klik multi-selection highlight - a bright
	// edge-overlay color plus forced-visible edges (see the onBeforeRender
	// override in loadIfcFile), deliberately never touching mesh.material's
	// own fill color so it composes with Feature G's coloring instead of
	// overwriting it (the spec's own "valgt/hover-element skal fortsat
	// kunne identificeres tydeligt oven på farvelægningen").
	setExpressIdSelected(expressID, selected){
		for(let mesh of this.meshesByExpressId.get(expressID) || []){
			mesh.userData.selected = selected;
			if(mesh.userData.edges){
				mesh.userData.edges.material.color.setHex(selected ? 0xffcc00 : 0x000000);
			}
		}
	}

	// Applies (or clears) a filter's result set in one pass over every
	// element the model actually has, rather than the caller diffing
	// old-vs-new matches itself. matchedExpressIds === null means "no active
	// filter" - every element is restored to its plain manually-set
	// visibility with no ghosting. mode is "hide" or "ghost", deciding what
	// happens to elements *not* in matchedExpressIds while a filter is
	// active.
	applyFilterVisibility(matchedExpressIds, mode){
		for(let expressID of this.meshesByExpressId.keys()){
			let matches = matchedExpressIds === null || matchedExpressIds.has(expressID);
			this.setExpressIdFilterHidden(expressID, !matches && mode === "hide");
			this.setExpressIdGhosted(expressID, !matches && mode === "ghost");
		}
	}

	// World-space bounding box covering just the given elements (not the
	// whole model) - for framing the camera on a single tree item or a
	// whole category group.
	getWorldBoundsFor(expressIds){
		let box = new THREE.Box3();
		for(let expressID of expressIds){
			for(let mesh of this.meshesByExpressId.get(expressID) || []){
				if(!mesh.visible){
					continue;
				}
				box.union(new THREE.Box3().setFromObject(mesh));
			}
		}
		return box;
	}

	dispose(){
		if(this.parent){
			this.parent.remove(this);
		}

		for(let mesh of this.meshes){
			mesh.geometry.dispose();
			mesh.material.dispose();
			if(mesh.userData.edges){
				mesh.userData.edges.geometry.dispose();
				mesh.userData.edges.material.dispose();
			}
		}

		if(this.modelID !== null && ifcAPI){
			try{
				ifcAPI.CloseModel(this.modelID);
			}catch(e){
				// already closed / API not initialized - fine to ignore on cleanup
			}
			geometryCacheByModel.delete(this.modelID);
		}
	}
}

export async function loadIfcFile(arrayBuffer){
	let api = await ensureIfcAPI();

	// COORDINATE_TO_ORIGIN is deliberately left off: it turned out to apply
	// an undocumented, inconsistent internal rebasing to LoadAllGeometry's
	// flatTransformation matrices (confirmed empirically - identical files
	// with the flag on came back with the site's real placement baked in
	// for some elements and rebased to near-zero for others, with no way to
	// know which from the outside), which is exactly the "vises med lokale
	// koordinater" bug this was rewritten to fix. Without it,
	// flatTransformation reliably reflects the *exact* IfcLocalPlacement
	// hierarchy authored in the file (already unit-converted to metres by
	// web-ifc internally, regardless of the file's declared length unit) -
	// real per-element geometry stays small/local in its own definition
	// (float32-safe; confirmed against real project files up to a few
	// thousand units, nowhere near float32's precision ceiling), while the
	// large absolute translation lives only in each mesh's own (double-
	// precision) placement matrix.
	let modelID = api.OpenModel(new Uint8Array(arrayBuffer), {COORDINATE_TO_ORIGIN: false});

	let model = new IFCModel();
	model.modelID = modelID;

	// web-ifc always hands back geometry/placements in Y-up space (its
	// output rotates IFC's native Z-up X/Y/Z into X/Z/-Y, matching the
	// Three.js/glTF convention its own default consumers expect) -
	// confirmed by comparing a chamber's known real-world IFC site
	// placement (X=572089.65 easting, Y=6219755.61 northing, Z=34.93
	// elevation) against what came back here (572089.65, 34.93,
	// -6219755.61): X unchanged, elevation became the "up" axis, northing
	// became -Z. Potree's world is Z-up throughout (every other module in
	// this app - LER, Digitizing, CutFillVolume - treats Z as elevation,
	// and Potree's own View.js rotates yaw around the Z axis), so without
	// correcting this an IFC model loads "on its side" relative to
	// everything else in the scene. A single +90 degree rotation around X
	// undoes exactly that: (x,y,z) -> (x,-z,y), i.e. elevation goes back
	// to Z and northing goes back to (positive) Y.
	let axisCorrection = new THREE.Matrix4().makeRotationX(Math.PI / 2);

	// The model's own IfcSite placement hierarchy is, per the above, already
	// baked into every element's flatTransformation - so the *only* extra
	// transform the container itself ever needs is IfcMapConversion's (when
	// present), which sits *outside* the ordinary placement hierarchy by
	// design and is never folded into it automatically. See
	// IfcGeoreference.js for the full priority order (IfcMapConversion >
	// IfcSite.ObjectPlacement > IfcSite.RefLatitude/RefLongitude > a logged
	// default assumption of EPSG:25832/DVR90).
	let georef = resolveIfcGeoreference(api, modelID, webIfcNamespace);
	model.georeference = georef;

	console.log(`[IFC] Georeferering for indlæst model:\n  ${georef.lines.join("\n  ")}\n  CRS: ${georef.crsLabel}, højdesystem: ${georef.verticalDatum} (kilde: ${georef.source})`);

	let worldMatrix = new THREE.Matrix4().multiplyMatrices(georef.matrix, axisCorrection);

	let position = new THREE.Vector3();
	let quaternion = new THREE.Quaternion();
	let scale = new THREE.Vector3();
	worldMatrix.decompose(position, quaternion, scale);
	model.position.copy(position);
	model.quaternion.copy(quaternion);
	model.scale.copy(scale);

	let flatMeshes = api.LoadAllGeometry(modelID);
	for(let flatMesh of flatMeshes){
		let geometries = flatMesh.geometries;
		for(let i = 0; i < geometries.size(); i++){
			let placedGeometry = geometries.get(i);
			let {geometry, edgesGeometry, localRadius} = buildGeometry(api, modelID, placedGeometry.geometryExpressID);

			let color = placedGeometry.color;
			let material = new THREE.MeshBasicMaterial({
				color: new THREE.Color(color.x, color.y, color.z),
				transparent: color.w < 1,
				opacity: color.w,
				side: THREE.DoubleSide,
				depthWrite: color.w >= 1,
			});

			let mesh = new THREE.Mesh(geometry, material);
			mesh.matrixAutoUpdate = false;
			mesh.matrix.fromArray(placedGeometry.flatTransformation);
			mesh.userData.expressID = flatMesh.expressID;
			mesh.userData.baseOpacity = color.w;
			// The element's own IFC-authored color, captured once so Feature
			// G (color by property) can restore it later without a model
			// reload - see setElementColor/clearAllElementColors.
			mesh.userData.baseColor = material.color.clone();

			let edges = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({color: 0x000000}));
			mesh.userData.edges = edges;
			mesh.add(edges);

			// Same wireframe-fade fix as LER.js's addEdgesOverlay - see
			// EDGES_MIN_APPARENT_RADIUS_PX above. mesh.matrixAutoUpdate is
			// false (matrix is set directly from flatTransformation above),
			// but matrixWorld is still kept in sync by the renderer's normal
			// per-frame scene.updateMatrixWorld() regardless, so
			// getWorldPosition/getWorldScale stay correct here.
			mesh.onBeforeRender = (renderer, scene, camera) => {
				// Feature H's selection highlight (setExpressIdSelected) always
				// wins over the apparent-size fade below - a selected element
				// should stay clearly identifiable regardless of how small/far
				// it currently renders, matching the spec's "valgt/hover-
				// element skal fortsat kunne identificeres tydeligt".
				if(mesh.userData.selected){
					edges.visible = true;
					return;
				}
				mesh.getWorldPosition(scratchWorldPos);
				mesh.getWorldScale(scratchWorldScale);
				let worldRadius = localRadius * Math.max(scratchWorldScale.x, scratchWorldScale.y, scratchWorldScale.z);
				let px = Utils.apparentPixelSize(worldRadius, scratchWorldPos, camera, renderer);
				edges.visible = px >= EDGES_MIN_APPARENT_RADIUS_PX;
			};

			model.add(mesh);
			model.meshes.push(mesh);

			if(!model.meshesByExpressId.has(flatMesh.expressID)){
				model.meshesByExpressId.set(flatMesh.expressID, []);
			}
			model.meshesByExpressId.get(flatMesh.expressID).push(mesh);
		}
	}

	return model;
}

// A plain textured mesh (Wavefront OBJ + optional MTL/textures) - for site
// scans and photogrammetry meshes (drone/handheld capture of an excavation,
// a borehole, ...) loaded alongside IFC models in the same section. Shares
// just enough of IFCModel's shape (meshes[], setOpacity, dispose, and
// modelID staying present-but-null) for the existing viewer UI - opacity
// slider, visibility toggle, fly-to, click-to-inspect's "no IFC here" guard -
// to drive either kind interchangeably without caring which one is loaded.
export class MeshModel extends THREE.Object3D{

	constructor(){
		super();
		this.name = "mesh_model";
		this.modelID = null;
		this.opacity = 1.0;
		this.meshes = [];
		this.objectUrls = [];
	}

	setOpacity(opacity){
		this.opacity = opacity;
		for(let mesh of this.meshes){
			mesh.material.opacity = opacity;
			mesh.material.transparent = opacity < 1;
			mesh.material.depthWrite = opacity >= 1;
		}
	}

	dispose(){
		if(this.parent){
			this.parent.remove(this);
		}

		for(let mesh of this.meshes){
			mesh.geometry.dispose();
			mesh.material.dispose();
		}

		for(let url of this.objectUrls){
			URL.revokeObjectURL(url);
		}
	}
}

// Loads a Wavefront OBJ mesh, with an optional companion MTL (+ its texture
// images) picked from the same file-input selection - a browser file input
// only ever hands back in-memory File objects, no real path to fetch
// relative references from, so every non-OBJ/MTL file in the selection is
// turned into a blob: URL and resolved by plain filename via a
// LoadingManager URL modifier (matching however the .obj/.mtl referenced it,
// regardless of any path prefix baked into that reference).
export async function loadMeshFile(files){
	let fileList = Array.from(files);
	let objFile = fileList.find(f => /\.obj$/i.test(f.name));
	if(!objFile){
		throw new Error(t("Ingen .obj-fil valgt."));
	}
	let mtlFile = fileList.find(f => /\.mtl$/i.test(f.name));

	let blobUrlByName = new Map();
	for(let file of fileList){
		if(file !== objFile && file !== mtlFile){
			blobUrlByName.set(file.name.toLowerCase(), URL.createObjectURL(file));
		}
	}

	let manager = new THREE.LoadingManager();
	manager.setURLModifier((url) => {
		let name = url.split(/[\\/]/).pop().toLowerCase();
		return blobUrlByName.get(name) || url;
	});

	let materials = null;
	if(mtlFile){
		let mtlText = await mtlFile.text();
		materials = new MTLLoader(manager).parse(mtlText, "");
		materials.preload();
	}

	let objLoader = new OBJLoader(manager);
	if(materials){
		objLoader.setMaterials(materials);
	}

	let objText = await objFile.text();
	let group = objLoader.parse(objText);

	let model = new MeshModel();
	model.objectUrls = [...blobUrlByName.values()];

	// OBJ has no fixed up-axis convention the way IFC does (there's no
	// analogous IfcSite/IfcProjectedCRS placement to read it from) - this
	// applies the same Y-up -> Z-up rotation used for web-ifc's output (see
	// loadIfcFile's axisCorrection above), since that matches the convention
	// Three.js's own OBJLoader assumes. If a given file turns out to already
	// be authored Z-up, this default will show it lying on its side.
	model.rotation.x = Math.PI / 2;
	model.add(group);

	// MeshBasicMaterial (unlit), same reasoning as IFC/LER: Potree's scene
	// has no THREE.Light set up. The source MTL's own opacity (Tr/d) is
	// deliberately NOT carried over - real-world exports from photogrammetry
	// tools routinely write a boilerplate "Tr 1.000000" that (per the OBJ
	// spec's dissolve-factor convention MTLLoader implements) parses to
	// opacity 0, silently making an intended-to-be-solid textured mesh
	// invisible. Opacity here is purely the UI slider's, same as every other
	// overlay layer in this app.
	group.traverse((child) => {
		if(!child.isMesh){
			return;
		}

		let source = Array.isArray(child.material) ? child.material[0] : child.material;
		let color = (source && source.color) ? source.color : new THREE.Color(0xaaaaaa);
		let map = source ? source.map : null;

		child.material = new THREE.MeshBasicMaterial({color, map: map || null, side: THREE.DoubleSide});
		model.meshes.push(child);
	});

	return model;
}

// Human-readable name + flattened property sets (Pset_* / DANDAS_* etc.)
// for one IFC element, for a click-to-inspect tooltip - mirrors
// formatLERFeatureInfo's role for LER features.
export async function getElementInfo(modelID, expressID){
	let api = await ensureIfcAPI();

	let item = await api.properties.getItemProperties(modelID, expressID);
	let psets = await api.properties.getPropertySets(modelID, expressID, true);

	let propertySets = [];
	for(let pset of psets){
		let setName = (pset.Name && pset.Name.value) || t("Egenskaber");
		let props = [];
		for(let prop of (pset.HasProperties || [])){
			if(!prop.Name || prop.NominalValue === null || prop.NominalValue === undefined){
				continue;
			}
			// NominalValue is usually a typed IFC value wrapper ({value: ...}),
			// but can come back as a bare primitive - handle both.
			let value = (typeof prop.NominalValue === "object") ? prop.NominalValue.value : prop.NominalValue;
			if(value !== null && value !== undefined){
				props.push({label: prop.Name.value, value});
			}
		}
		if(props.length > 0){
			propertySets.push({name: setName, properties: props});
		}
	}

	return {
		name: (item.Name && item.Name.value) || null,
		globalId: (item.GlobalId && item.GlobalId.value) || null,
		propertySets,
	};
}

// The IFC spatial tree (Project > Site > Building > Storey > elements),
// for a layer/visibility panel like the one in BIMvision's "IFC structure"
// pane - see getSpatialStructure's Node shape: {expressID, type, children,
// Name (present because includeProperties=true is passed)}.
export async function getSpatialTree(modelID){
	let api = await ensureIfcAPI();
	return api.properties.getSpatialStructure(modelID, true);
}

// Pure organizational levels of the spatial tree - never filterable
// elements in their own right (an IfcSpace *is* indexed as a filterable
// element too, in addition to acting as a container, matching how the
// existing "Struktur" tree already treats it).
const IFC_HIERARCHY_ONLY_TYPES = new Set(["IFCPROJECT", "IFCSITE", "IFCBUILDING", "IFCBUILDINGSTOREY"]);

function unwrapIfcValue(raw){
	if(raw === null || raw === undefined){
		return null;
	}
	let value = (typeof raw === "object" && "value" in raw) ? raw.value : raw;
	if(value === null || value === undefined){
		return null;
	}
	if(typeof value === "number" || typeof value === "boolean"){
		return value;
	}
	return String(value);
}

// One row of a normalized property record - see Properties.HasProperties'
// own {Name, NominalValue} shape. Values are normalized to exactly text,
// number, boolean or null (F's own requirement) so the filter's operators
// (numeric comparison in particular) work regardless of how a given
// exporter wrote the value into IFC.
function normalizedProperty(name, rawValue){
	let value = unwrapIfcValue(rawValue);
	let type = value === null ? "null" : typeof value === "number" ? "number" : typeof value === "boolean" ? "boolean" : "text";
	return {name, value, type};
}

// Fetches many IFC lines in one WASM round-trip - api.GetLines(modelID,
// [id, id, ...]) batches internally (a single call into the wasm module),
// whereas api.properties.getItemProperties() only ever wraps a *single*
// line per call. That distinction turned out to be the whole story for
// indexing performance: a naive one-getItemProperties-call-per-relationship
// loop took ~220s on a real 5,444-element model (thousands of
// IfcRelDefinesByProperties/ByType relationships, each its own WASM
// round-trip); batching every relationship/pset/type lookup like this
// brought the same model down to a couple of seconds. Chunked (rather than
// one giant call) so onYield still gets a chance to run against huge
// models, and so a single WASM array argument never gets enormous.
const LINE_FETCH_CHUNK_SIZE = 2000;

async function fetchLinesChunked(api, modelID, ids, flatten, onYield){
	let out = new Map();
	for(let i = 0; i < ids.length; i += LINE_FETCH_CHUNK_SIZE){
		await onYield();
		let chunk = ids.slice(i, i + LINE_FETCH_CHUNK_SIZE);
		let lines = api.GetLines(modelID, chunk, flatten);
		for(let line of lines){
			out.set(line.expressID, line);
		}
	}
	return out;
}

function toIdArray(vec){
	let arr = new Array(vec.size());
	for(let i = 0; i < arr.length; i++){
		arr[i] = vec.get(i);
	}
	return arr;
}

function toValueArray(related){
	if(!related){
		return [];
	}
	return (Array.isArray(related) ? related : [related]).map(r => r.value);
}

// Flattened IfcPropertySet line -> {setName, properties: [{name,value,type}]}
function normalizedPropertySet(psetLine){
	let setName = (psetLine.Name && psetLine.Name.value) || t("Egenskaber");
	let properties = [];
	for(let prop of (psetLine.HasProperties || [])){
		if(prop && prop.Name){
			properties.push(normalizedProperty(prop.Name.value, prop.NominalValue));
		}
	}
	return {setName, properties};
}

// Walks IfcRelDefinesByProperties directly via the relationship objects
// themselves (RelatingPropertyDefinition -> the pset, RelatedObjects -> the
// elements it applies to - both are the relationship's own forward
// attributes, spelled the same way in IFC2x3 and IFC4), rather than calling
// properties.getPropertySets per element: the handful of *relationships* in
// a model is what actually needs enumerating, so this is a small, roughly
// constant number of WASM calls regardless of whether the model has 500 or
// 100,000 elements, instead of one round-trip per element per pset.
// psetCache is shared with collectTypePropertySetsByElement so a pset
// referenced both directly and via a type is only ever fetched once.
async function collectDirectPropertySetsByElement(api, modelID, onYield, psetCache){
	let byElement = new Map();

	let relIds = toIdArray(api.GetLineIDsWithType(modelID, webIfcNamespace.IFCRELDEFINESBYPROPERTIES));
	let relLines = await fetchLinesChunked(api, modelID, relIds, false, onYield);

	let neededPsetIds = new Set();
	for(let rel of relLines.values()){
		if(rel.RelatingPropertyDefinition){
			neededPsetIds.add(rel.RelatingPropertyDefinition.value);
		}
	}

	let psetIdsToFetch = Array.from(neededPsetIds).filter(id => !psetCache.has(id));
	let psetLines = await fetchLinesChunked(api, modelID, psetIdsToFetch, true, onYield);
	for(let [psetId, psetLine] of psetLines){
		psetCache.set(psetId, normalizedPropertySet(psetLine));
	}

	for(let rel of relLines.values()){
		let relating = rel.RelatingPropertyDefinition;
		let related = rel.RelatedObjects;
		if(!relating || !related){
			continue;
		}

		let pset = psetCache.get(relating.value);
		if(!pset || pset.properties.length === 0){
			continue;
		}

		for(let elementId of toValueArray(related)){
			if(!byElement.has(elementId)){
				byElement.set(elementId, []);
			}
			byElement.get(elementId).push(pset);
		}
	}

	return byElement;
}

// Walks IfcRelDefinesByType the same way, for properties an element only
// inherits from its shared type/catalogue entry (e.g. a pipe fitting's
// type-level manufacturer data) rather than carrying itself. RelatingType
// points at an IfcElementType, which holds its own psets indirectly via
// HasPropertySets - one extra indirection this needs and the direct-pset
// case above doesn't.
async function collectTypePropertySetsByElement(api, modelID, onYield, psetCache){
	let byElement = new Map();

	let relIds = toIdArray(api.GetLineIDsWithType(modelID, webIfcNamespace.IFCRELDEFINESBYTYPE));
	let relLines = await fetchLinesChunked(api, modelID, relIds, false, onYield);

	let neededTypeIds = new Set();
	for(let rel of relLines.values()){
		if(rel.RelatingType){
			neededTypeIds.add(rel.RelatingType.value);
		}
	}

	let typeLines = await fetchLinesChunked(api, modelID, Array.from(neededTypeIds), false, onYield);

	let neededPsetIds = new Set();
	for(let typeLine of typeLines.values()){
		for(let p of (typeLine.HasPropertySets || [])){
			neededPsetIds.add(p.value);
		}
	}

	let psetIdsToFetch = Array.from(neededPsetIds).filter(id => !psetCache.has(id));
	let psetLines = await fetchLinesChunked(api, modelID, psetIdsToFetch, true, onYield);
	for(let [psetId, psetLine] of psetLines){
		psetCache.set(psetId, normalizedPropertySet(psetLine));
	}

	let psetsByType = new Map();
	for(let [typeId, typeLine] of typeLines){
		let psets = [];
		for(let p of (typeLine.HasPropertySets || [])){
			let pset = psetCache.get(p.value);
			if(pset && pset.properties.length > 0){
				psets.push(pset);
			}
		}
		psetsByType.set(typeId, psets);
	}

	for(let rel of relLines.values()){
		let relatingType = rel.RelatingType;
		let related = rel.RelatedObjects;
		if(!relatingType || !related){
			continue;
		}

		let psets = psetsByType.get(relatingType.value) || [];
		if(psets.length === 0){
			continue;
		}

		for(let elementId of toValueArray(related)){
			if(!byElement.has(elementId)){
				byElement.set(elementId, []);
			}
			byElement.get(elementId).push(...psets);
		}
	}

	return byElement;
}

// Builds a searchable per-element property index for the *whole* model in
// a small, bounded number of WASM round-trips (see
// collectPropertySetsByElement) rather than one getPropertySets call per
// element - the difference between indexing a 100,000-element model taking
// seconds versus minutes. onYield is awaited periodically so long-running
// indexing doesn't lock up the render loop; the caller (index.html) drives
// this via a plain setTimeout(0) since web-ifc's WASM state isn't
// transferable into a Web Worker.
//
// Returns Map<expressID, {ifcType, name, globalId, building, storey,
// propertySets: [{setName, properties: [{name, value, type}]}]}>
export async function buildPropertyIndex(modelID, onYield = () => new Promise(r => setTimeout(r, 0))){
	let api = await ensureIfcAPI();

	// includeProperties=false here deliberately - the built-in true branch
	// would call getItemProperties() (one un-batched GetLine call) per node,
	// the exact one-round-trip-per-line pattern this whole file is written
	// to avoid elsewhere. Fetching bare {expressID,type,children} first and
	// then batch-loading every node's Name/GlobalId in one pass (below) cut
	// this step from ~6.7s to well under a second on a 5,444-element
	// real-world model.
	let tree = await api.properties.getSpatialStructure(modelID, false);

	let allNodeIds = [];
	let collectIds = (node) => {
		if(node.expressID !== undefined){
			allNodeIds.push(node.expressID);
		}
		for(let child of node.children || []){
			collectIds(child);
		}
	};
	collectIds(tree);
	let nodeLines = await fetchLinesChunked(api, modelID, allNodeIds, false, onYield);

	let index = new Map();

	let walk = (node, spatialStack) => {
		let isHierarchyOnly = IFC_HIERARCHY_ONLY_TYPES.has((node.type || "").toUpperCase());
		let line = nodeLines.get(node.expressID);
		let nextStack = isHierarchyOnly ? [...spatialStack, {type: node.type, line}] : spatialStack;

		if(!isHierarchyOnly && node.expressID !== undefined){
			let building = nextStack.find(n => (n.type || "").toUpperCase() === "IFCBUILDING");
			let storey = nextStack.find(n => (n.type || "").toUpperCase() === "IFCBUILDINGSTOREY");

			index.set(node.expressID, {
				ifcType: node.type || null,
				name: (line && line.Name && line.Name.value) || null,
				globalId: (line && line.GlobalId && line.GlobalId.value) || null,
				building: building ? ((building.line && building.line.Name && building.line.Name.value) || null) : null,
				storey: storey ? ((storey.line && storey.line.Name && storey.line.Name.value) || null) : null,
				propertySets: [],
			});
		}

		for(let child of node.children || []){
			walk(child, nextStack);
		}
	};
	walk(tree, []);

	let mergeInto = (byElement) => {
		for(let [elementId, psets] of byElement){
			let entry = index.get(elementId);
			if(entry){
				entry.propertySets.push(...psets);
			}
		}
	};

	let psetCache = new Map(); // shared across both passes - see collectDirectPropertySetsByElement
	mergeInto(await collectDirectPropertySetsByElement(api, modelID, onYield, psetCache));
	mergeInto(await collectTypePropertySetsByElement(api, modelID, onYield, psetCache));

	return index;
}

// Stop collecting distinct values for a field past this many - a picker
// with more options than that isn't usable as a checkbox list anyway, and
// a near-unique-per-element field (like GlobalId) would otherwise grow
// without bound for no benefit.
const CATALOG_DISTINCT_VALUE_CAP = 200;

// Feature F's filter panel needs to know, up front, which fields exist to
// filter on at all and what kind of value each one holds - this is that
// catalog, derived once from a buildPropertyIndex() result. Distinct from
// the index itself: the index is per-element ("what does THIS element
// have"), the catalog is per-model ("what CAN elements have, and which few
// values has this model actually shown for it" - the small-cardinality
// value picker's source). "model/fil" isn't included here since it's
// caller-side (file name) metadata the index has no way to know, not IFC
// data - the caller adds that field itself.
//
// Returns Map<key, {key, label, group, valueType, distinctValues: Set}>
// where key is "sys:<field>" for the always-exposed system fields or
// "prop:<psetName>::<propName>" for a property-set property.
export function buildPropertyCatalog(index){
	let fields = new Map();

	let touch = (key, label, group, valueType, value) => {
		let field = fields.get(key);
		if(!field){
			// First-seen type wins for a given key - real-world exporters are
			// consistent about a property's value type across elements of the
			// same kind in practice, and this avoids having to reconcile
			// conflicting types for comparatively little benefit.
			field = {key, label, group, valueType, distinctValues: new Set()};
			fields.set(key, field);
		}
		if(value !== null && value !== undefined && field.distinctValues.size < CATALOG_DISTINCT_VALUE_CAP){
			field.distinctValues.add(value);
		}
	};

	for(let entry of index.values()){
		touch("sys:ifcType", "IFC-type", t("Systemfelter"), "text", entry.ifcType);
		touch("sys:name", t("Navn"), t("Systemfelter"), "text", entry.name);
		touch("sys:globalId", "GlobalId", t("Systemfelter"), "text", entry.globalId);
		touch("sys:building", t("Bygning"), t("Systemfelter"), "text", entry.building);
		touch("sys:storey", t("Etage"), t("Systemfelter"), "text", entry.storey);

		for(let pset of entry.propertySets){
			for(let prop of pset.properties){
				let key = `prop:${pset.setName}::${prop.name}`;
				touch(key, prop.name, pset.setName, prop.type === "null" ? "text" : prop.type, prop.value);
			}
		}
	}

	return fields;
}

// Reads one field's value (and IFC-normalized type, for property-set
// properties) off a single buildPropertyIndex() entry, using the same key
// scheme as buildPropertyCatalog - the filter engine's one and only place
// that knows how a field key maps to actual element data, so evaluating a
// filter row and building the catalog can never disagree about a key's
// meaning. Returns null when the field isn't present on this element
// (distinct from it being present-but-empty) - the filter engine treats
// that as "no value to compare", never as a crash.
export function getFieldValue(entry, key){
	if(key === "sys:ifcType"){ return entry.ifcType; }
	if(key === "sys:name"){ return entry.name; }
	if(key === "sys:globalId"){ return entry.globalId; }
	if(key === "sys:building"){ return entry.building; }
	if(key === "sys:storey"){ return entry.storey; }

	if(key.startsWith("prop:")){
		let rest = key.slice("prop:".length);
		let sep = rest.indexOf("::");
		if(sep === -1){
			return null;
		}
		let setName = rest.slice(0, sep);
		let propName = rest.slice(sep + 2);

		for(let pset of entry.propertySets){
			if(pset.setName !== setName){
				continue;
			}
			for(let prop of pset.properties){
				if(prop.name === propName){
					return prop.value;
				}
			}
		}
	}

	return null;
}
